import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useMemo, useState } from 'react';

import { CompactCustomSkillRow } from './compact-custom-skill-row';
import { CustomSkillDetailView } from './custom-skill-detail-view';
import { getRootCustomSkillUuid, type CustomSkillManagementTab } from './custom-skill-model';
import { CustomSkillsCard } from './custom-skills-card';
import { CustomSkillsPage } from './custom-skills-page';
import { DeleteCustomSkillDialog } from './delete-custom-skill-dialog';
import { SettingsPageCustomSkillRow } from './settings-page-custom-skill-row';
import { useCustomSkillActions } from './use-custom-skill-actions';
import { useCustomSkillCollections } from './use-custom-skill-collections';
import { CATEGORY_DISPLAY_NAMES } from '../../../../lib/skill-constants';
import type { CustomSkillRecord } from '../../../../queries/chats';
import { useChatOverlayContextSafe } from '../../contexts/chat-overlay-context';
import { useCustomSkillDownload } from '../custom-skill-download';
import { SkillCategorySection } from '../skill-list-item';
import { SkillShareDialog } from './sharing/skill-share-dialog';
import { WtwSkillRow } from './wtw-skill-row';
import { useNovaContext } from '../../providers';
import { CreateSkillView } from '../custom-skills-ui';

export type CustomSkillsLayout = 'card' | 'page';

export function CustomSkillsManageView({
  layout,
  onAddSkill,
  onEditSkill,
}: {
  layout: CustomSkillsLayout;
  onAddSkill?: () => void;
  onEditSkill?: (skill: CustomSkillRecord) => void;
}) {
  const { userUuid } = useNovaContext();
  const chatOverlayContext = useChatOverlayContextSafe();
  const showMySkills = useFeatureFlag('nova-skills-management', false);
  const canManageSharing = useFeatureFlag('nova-skills-sharing', false);
  const [isCreateViewOpen, setIsCreateViewOpen] = useState(false);
  const [skillToUpdate, setSkillToUpdate] = useState<CustomSkillRecord | null>(null);
  const [selectedTab, setSelectedTab] = useState<CustomSkillManagementTab>(showMySkills ? 'my-skills' : 'wtw-skills');
  const [expandedSkillUuids, setExpandedSkillUuids] = useState<string[]>([]);
  const [skillToDelete, setSkillToDelete] = useState<CustomSkillRecord | null>(null);
  const [skillToShare, setSkillToShare] = useState<CustomSkillRecord | null>(null);
  const [skillToView, setSkillToView] = useState<CustomSkillRecord | null>(null);
  const { downloadCustomSkill, isDownloadingCustomSkill } = useCustomSkillDownload();
  const {
    error,
    groupedWtwSkillCommands,
    isInitialLoading,
    mySkills,
    refreshAllSkills,
    reloadSkills,
    runtimeSkillsByUuid,
    sharedSkills,
    skills,
    wtwSkillCommands,
  } = useCustomSkillCollections({
    accountUuid: chatOverlayContext?.accountUuid,
    userUuid,
    showMySkills,
  });
  const showSharedSkills = canManageSharing || sharedSkills.length > 0;
  const activeTab =
    selectedTab === 'my-skills' && !showMySkills
      ? 'wtw-skills'
      : selectedTab === 'shared-with-me' && !showSharedSkills
        ? showMySkills
          ? 'my-skills'
          : 'wtw-skills'
        : selectedTab;
  const { isSkillPending, pendingAction, publishDraft, removeSkill, toggleSkill } = useCustomSkillActions({
    showMySkills,
    showSharedSkills,
    refreshAllSkills,
  });

  const activeSkills = activeTab === 'shared-with-me' ? sharedSkills : activeTab === 'my-skills' ? mySkills : [];
  const viewedSkill = useMemo(() => {
    if (!skillToView) {
      return null;
    }

    const viewedRootSkillUuid = getRootCustomSkillUuid(skillToView);
    return skills.find((skill) => getRootCustomSkillUuid(skill) === viewedRootSkillUuid) ?? skillToView;
  }, [skillToView, skills]);
  const activeEmptyMessage =
    activeTab === 'shared-with-me'
      ? 'No skills have been shared with you yet.'
      : activeTab === 'wtw-skills'
        ? 'No WTW skills are available yet.'
        : 'No custom skills yet. Add one from the header above or use /skill-creator in chat to draft a new skill.';

  const openCreateView = () => {
    if (!showMySkills) {
      return;
    }

    if (onAddSkill) {
      onAddSkill();
      return;
    }

    setSkillToUpdate(null);
    setIsCreateViewOpen(true);
  };

  const openUpdateView = (skill: CustomSkillRecord) => {
    if (!showMySkills) {
      return;
    }

    if (onEditSkill) {
      onEditSkill(skill);
      return;
    }

    setIsCreateViewOpen(false);
    setSkillToUpdate(skill);
  };

  const isSkillExpanded = (uuid: string) => expandedSkillUuids.includes(uuid);

  const toggleExpandedSkill = (uuid: string) => {
    setExpandedSkillUuids((currentUuids) =>
      currentUuids.includes(uuid)
        ? currentUuids.filter((currentUuid) => currentUuid !== uuid)
        : [...currentUuids, uuid],
    );
  };

  const handleToggle = async (skill: CustomSkillRecord) => {
    const updatedSkill = await toggleSkill(skill);
    if (updatedSkill) {
      setSkillToView((currentSkill) => (currentSkill?.uuid === skill.uuid ? updatedSkill : currentSkill));
    }
  };

  const handleDelete = async () => {
    if (!skillToDelete) {
      return;
    }

    const deletedRootSkillUuid = getRootCustomSkillUuid(skillToDelete);
    if (await removeSkill(skillToDelete)) {
      setSkillToView((currentSkill) =>
        currentSkill && getRootCustomSkillUuid(currentSkill) === deletedRootSkillUuid ? null : currentSkill,
      );
      setSkillToDelete(null);
    }
  };

  const retryActiveTab = () => {
    void (activeTab === 'wtw-skills' ? refreshAllSkills() : reloadSkills());
  };

  const customSkillDialogs = (
    <>
      <DeleteCustomSkillDialog
        skill={showMySkills ? skillToDelete : null}
        isDeleting={pendingAction?.type === 'delete'}
        onClose={() => setSkillToDelete(null)}
        onConfirm={() => void handleDelete()}
      />
      {canManageSharing && skillToShare ? (
        <SkillShareDialog
          key={skillToShare.uuid}
          skill={skillToShare}
          onClose={() => setSkillToShare(null)}
          onChanged={refreshAllSkills}
        />
      ) : null}
    </>
  );

  if (showMySkills && isCreateViewOpen) {
    return <CreateSkillView onBack={() => setIsCreateViewOpen(false)} />;
  }

  if (showMySkills && skillToUpdate) {
    return <CreateSkillView onBack={() => setSkillToUpdate(null)} skillToUpdate={skillToUpdate} />;
  }

  if (layout === 'page' && showMySkills && viewedSkill) {
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col gap-6 bg-white">
        <CustomSkillDetailView
          skill={viewedSkill}
          isTogglePending={isSkillPending(viewedSkill.uuid)}
          isPublishing={pendingAction?.type === 'publish' && pendingAction.uuid === viewedSkill.uuid}
          isDownloading={isDownloadingCustomSkill(viewedSkill.uuid)}
          showSharingControls={canManageSharing}
          onBack={() => setSkillToView(null)}
          onDownload={() => void downloadCustomSkill(viewedSkill)}
          onEdit={() => openUpdateView(viewedSkill)}
          onToggle={() => void handleToggle(viewedSkill)}
          onSharingChanged={refreshAllSkills}
          onDelete={() => setSkillToDelete(viewedSkill)}
          onPublishDraft={() => void publishDraft(viewedSkill)}
        />
        {customSkillDialogs}
      </div>
    );
  }

  const pageContent =
    activeTab === 'wtw-skills' && wtwSkillCommands.length === 0 ? (
      <EmptyPageState>{activeEmptyMessage}</EmptyPageState>
    ) : activeTab === 'wtw-skills' ? (
      <div className="overflow-hidden rounded-[4px] border border-[#DEE2E7] bg-white">
        {groupedWtwSkillCommands.map((group) => (
          <SkillCategorySection
            key={group.category}
            title={CATEGORY_DISPLAY_NAMES[group.category] || group.category}
            countLabel={`${group.commands.length} ${group.commands.length === 1 ? 'skill' : 'skills'}`}
          >
            {group.commands.map((command) => (
              <WtwSkillRow
                key={command.id}
                command={command}
                isExpanded={isSkillExpanded(command.id)}
                onToggleExpanded={() => toggleExpandedSkill(command.id)}
              />
            ))}
          </SkillCategorySection>
        ))}
      </div>
    ) : activeSkills.length === 0 ? (
      <EmptyPageState>{activeEmptyMessage}</EmptyPageState>
    ) : (
      <div className="overflow-hidden rounded-[4px] border border-[#DEE2E7] bg-white">
        {activeSkills.map((skill) => (
          <SettingsPageCustomSkillRow
            key={skill.uuid}
            skill={skill}
            listScope={activeTab}
            isPending={isSkillPending(skill.uuid)}
            isExpanded={activeTab === 'shared-with-me' && isSkillExpanded(skill.uuid)}
            onOpen={() => setSkillToView(skill)}
            onToggle={() => void handleToggle(skill)}
            onToggleExpanded={() => toggleExpandedSkill(skill.uuid)}
          />
        ))}
      </div>
    );

  if (layout === 'page') {
    return (
      <CustomSkillsPage
        activeTab={activeTab}
        mySkillCount={mySkills.length}
        sharedSkillCount={sharedSkills.length}
        showMySkills={showMySkills}
        showSharedSkills={showSharedSkills}
        isLoading={isInitialLoading}
        error={error}
        onAddSkill={openCreateView}
        onRetry={retryActiveTab}
        onTabChange={setSelectedTab}
      >
        {pageContent}
        {customSkillDialogs}
      </CustomSkillsPage>
    );
  }

  const renderCompactSkillRow = (skill: CustomSkillRecord, listScope: CustomSkillManagementTab = 'my-skills') => (
    <CompactCustomSkillRow
      key={skill.uuid}
      skill={skill}
      listScope={listScope}
      runtimeSkill={runtimeSkillsByUuid.get(skill.uuid)}
      pendingAction={pendingAction}
      isDownloading={isDownloadingCustomSkill(skill.uuid)}
      isExpanded={isSkillExpanded(skill.uuid)}
      showSharingControls={canManageSharing}
      onToggle={() => void handleToggle(skill)}
      onToggleExpanded={() => toggleExpandedSkill(skill.uuid)}
      onDownload={() => void downloadCustomSkill(skill)}
      onEdit={() => openUpdateView(skill)}
      onShare={() => setSkillToShare(skill)}
      onDelete={() => setSkillToDelete(skill)}
    />
  );
  const cardContent =
    activeTab === 'wtw-skills' && wtwSkillCommands.length === 0 ? (
      <EmptyCardState>{activeEmptyMessage}</EmptyCardState>
    ) : activeTab === 'wtw-skills' ? (
      groupedWtwSkillCommands.map((group) => (
        <SkillCategorySection
          key={group.category}
          title={CATEGORY_DISPLAY_NAMES[group.category] || group.category}
          countLabel={`${group.commands.length} ${group.commands.length === 1 ? 'skill' : 'skills'}`}
        >
          {group.commands.map((command) => (
            <WtwSkillRow
              key={command.id}
              command={command}
              isExpanded={isSkillExpanded(command.id)}
              onToggleExpanded={() => toggleExpandedSkill(command.id)}
            />
          ))}
        </SkillCategorySection>
      ))
    ) : activeSkills.length === 0 ? (
      <EmptyCardState>{activeEmptyMessage}</EmptyCardState>
    ) : activeTab === 'shared-with-me' ? (
      activeSkills.map((skill) => renderCompactSkillRow(skill, 'shared-with-me'))
    ) : (
      activeSkills.map((skill) => renderCompactSkillRow(skill))
    );

  return (
    <CustomSkillsCard
      activeTab={activeTab}
      mySkillCount={mySkills.length}
      sharedSkillCount={sharedSkills.length}
      showMySkills={showMySkills}
      showSharedSkills={showSharedSkills}
      isLoading={isInitialLoading}
      error={error}
      onRetry={retryActiveTab}
      onTabChange={setSelectedTab}
    >
      {cardContent}
      {customSkillDialogs}
    </CustomSkillsCard>
  );
}

function EmptyPageState({ children }: { children: string }) {
  return (
    <div className="rounded-[4px] border border-[#DEE2E7] bg-white px-4 py-5">
      <p className="text-sm leading-5 text-[#546A83]">{children}</p>
    </div>
  );
}

function EmptyCardState({ children }: { children: string }) {
  return (
    <div className="px-5 pt-4 pb-5">
      <p className="text-muted-foreground text-sm leading-6">{children}</p>
    </div>
  );
}
